import { BALL_RADIUS, BALL_MASS } from './constants.js'

export type BallState = 'rolling' | 'stopped'

export interface Vec2 {
  x: number
  y: number
}

export class Ball {
  /** Position in metres */
  pos: Vec2
  /** Velocity in m/s */
  vel: Vec2
  /**
   * Angular velocity (rad/s around z-axis for top-down 2D).
   * Tracked for future spin effects; not yet used in physics.
   */
  angularVel: number
  readonly radius: number
  readonly mass: number
  state: BallState

  constructor(x: number, y: number, radius = BALL_RADIUS, mass = BALL_MASS) {
    this.pos = { x, y }
    this.vel = { x: 0, y: 0 }
    this.angularVel = 0
    this.radius = radius
    this.mass = mass
    this.state = 'stopped'
  }

  get speed(): number {
    return Math.sqrt(this.vel.x ** 2 + this.vel.y ** 2)
  }

  /** Unit vector of current velocity direction. Returns {0,0} if stopped. */
  get direction(): Vec2 {
    const s = this.speed
    if (s < 1e-9) return { x: 0, y: 0 }
    return { x: this.vel.x / s, y: this.vel.y / s }
  }

  /**
   * Apply a shot: set velocity from angle (radians) and speed (m/s).
   * Angle 0 = +x direction, increases anticlockwise.
   */
  shoot(angle: number, speed: number): void {
    this.vel.x = Math.cos(angle) * speed
    this.vel.y = Math.sin(angle) * speed
    this.state = 'rolling'
  }

  stop(): void {
    this.vel.x = 0
    this.vel.y = 0
    this.angularVel = 0
    this.state = 'stopped'
  }

  clone(): Ball {
    const b = new Ball(this.pos.x, this.pos.y, this.radius, this.mass)
    b.vel = { ...this.vel }
    b.angularVel = this.angularVel
    b.state = this.state
    return b
  }
}
